import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Meal, Venue } from '../../../types';
import type { PlaceBasicDetails } from '../../../api/places.api';
import MealDetails from './MealDetails';
import type { IngredientActions } from './MealIngredients';
import type { MealStopActions } from './MealStopLink';
import { MapPin, Pencil, Route, X, Icon } from '../../ui/Icon';

interface Props {
  meal: Meal;
  venues: Venue[];
  readOnly: boolean;
  onRename: (title: string) => void;
  onUpdate: (patch: { notes?: string | null; venueId?: string | null }) => Promise<boolean>;
  onDelete: () => void;
  onPickPlace: (place: PlaceBasicDetails) => Promise<void>;
  ingredients: IngredientActions;
  stops: MealStopActions;
  detailsOpen: boolean;
  onToggleDetails: () => void;
}

/**
 * One planned meal. Edited in place — a modal for "pasta con salmón" is more
 * ceremony than the thing it captures.
 */
export default function MealRow({
  meal, venues, readOnly, onRename, onUpdate, onDelete, onPickPlace, ingredients, stops, detailsOpen, onToggleDetails,
}: Props) {
  const { t } = useTranslation();

  // The draft doubles as the "is editing" flag, like PackingItemRow: null =
  // not editing, so there is no second boolean that can disagree with it.
  const [draft, setDraft] = useState<string | null>(null);
  // Escape has to beat the blur it triggers, or the input losing focus commits
  // the very draft the user just discarded.
  const discarded = useRef(false);

  function commit(value: string) {
    if (discarded.current) return;
    setDraft(null);
    const title = value.trim();
    if (title && title !== meal.title) onRename(title);
  }

  function discard() {
    discarded.current = true;
    setDraft(null);
  }

  return (
    <div className="group py-0.5">
      <div className="flex items-start gap-1.5">
      {draft !== null ? (
        <input
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={e => commit(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') commit(e.currentTarget.value);
            if (e.key === 'Escape') discard();
          }}
          className="flex-1 bg-transparent border-b border-teal-400 dark:border-teal-500 text-sm text-slate-800 dark:text-slate-100 focus:outline-none"
        />
      ) : (
        <button
          onClick={() => { if (!readOnly) { discarded.current = false; setDraft(meal.title); } }}
          disabled={readOnly}
          className="flex-1 min-w-0 text-left text-sm leading-snug break-words cursor-text text-slate-800 dark:text-slate-100 transition-all hover:underline decoration-dotted decoration-slate-300 underline-offset-4 disabled:no-underline disabled:cursor-default"
        >
          {meal.title}
          {meal.venue && (
            <span className="ml-1.5 inline-flex items-center gap-0.5 align-middle px-1.5 py-px rounded-full text-[10px] font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              <Icon as={MapPin} size={9} />
              {meal.venue.name}
            </span>
          )}
          {meal.activity && (
            <span
              title={t('meals.stop.linked')}
              className="ml-1.5 inline-flex items-center gap-0.5 align-middle px-1.5 py-px rounded-full text-[10px] font-medium bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300"
            >
              <Icon as={Route} size={9} />
              {meal.activity.startTime ?? t('meals.stop.badge')}
            </span>
          )}
          {meal.notes && (
            <span className="block text-[11px] text-slate-400 dark:text-slate-500 truncate">{meal.notes}</span>
          )}
          {meal.ingredients.length > 0 && (
            <span className="block text-[11px] text-teal-700/80 dark:text-teal-400/80 truncate">
              {meal.ingredients.map(i => `${i.pantryItem.name} ${i.label}`).join(' · ')}
            </span>
          )}
        </button>
      )}

      {!readOnly && draft === null && (
        <>
          {/* Always visible, unlike the delete button next to it. Behind this
              pencil is the only way to say a meal happens at a restaurant, and
              hiding it behind a hover meant nobody found it — and on a touch
              screen there is no hover to find it with. */}
          <button
            onClick={onToggleDetails}
            title={t('meals.editMeal')}
            className={`shrink-0 w-6 h-6 rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
              detailsOpen
                ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                : 'bg-slate-100 text-slate-500 hover:bg-teal-100 hover:text-teal-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-teal-900/30'
            }`}
            aria-label={t('meals.editMeal')}
            aria-expanded={detailsOpen}
          >
            <Icon as={Pencil} size={12} />
          </button>
          <button
            onClick={onDelete}
            className="shrink-0 w-6 h-6 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 bg-slate-100 text-slate-500 hover:bg-red-100 hover:text-red-600 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-colors cursor-pointer"
            aria-label={t('common.delete')}
          >
            <Icon as={X} size={12} />
          </button>
        </>
      )}
      </div>

      {detailsOpen && !readOnly && (
        <MealDetails meal={meal} venues={venues} onChange={onUpdate} onPickPlace={onPickPlace} ingredients={ingredients} stops={stops} onClose={onToggleDetails} />
      )}
    </div>
  );
}
